import type { DictationTurn } from '../types';

/** Orders turns and joins their text with single spaces, skipping empty ones. */
export function joinTurns(turns: DictationTurn[]): string {
  return [...turns]
    .sort((a, b) => a.order - b.order)
    .map(turn => turn.text.trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * Appends spoken text to what the field already held when dictation started.
 * A base that ends on a new line keeps that line break; otherwise one space
 * separates typed and spoken text.
 */
export function composeDictation(base: string, turns: DictationTurn[]): string {
  const spoken = joinTurns(turns);
  if (!spoken) return base;
  if (!base.trim()) return spoken;
  if (/\n[ \t]*$/.test(base)) return `${base.replace(/[ \t]+$/, '')}${spoken}`;
  return `${base.replace(/\s+$/, '')} ${spoken}`;
}

/**
 * Replaces the turn with the same order. A turn never regresses: once final it
 * ignores partial updates, and a formatted final replaces an unformatted one.
 */
export function mergeTurn(turns: DictationTurn[], incoming: DictationTurn): DictationTurn[] {
  const index = turns.findIndex(turn => turn.order === incoming.order);
  if (index === -1) return [...turns, incoming];
  const existing = turns[index];
  if (existing.final && !incoming.final) return turns;
  if (existing.final && existing.formatted && !incoming.formatted) return turns;
  const next = turns.slice();
  next[index] = incoming;
  return next;
}
